/**
 * /payroll-l2/equipment — Equipment register and time entries.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";

import { api } from "@/lib/api";
import type { LegacyEquipmentItem, EquipmentStatus } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/equipment")({
  component: EquipmentPage,
});

const STATUS_COLOR: Record<EquipmentStatus, "success" | "error" | "warning"> = {
  active: "success",
  retired: "error",
  maintenance: "warning",
};

function fmtMoney(v: string | null) {
  if (!v) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function EquipmentPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["payroll-l2", "equipment"],
    queryFn: async () => {
      return await api<LegacyEquipmentItem[]>("/api/payroll-l2/equipment");
    },
  });

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>Equipment</Typography>
        <HelpHint docPath="payroll-l2/equipment.md" label="Equipment costing help" />
      </Stack>

      {isLoading && <CircularProgress size={24} />}
      {error && <Alert severity="error">Failed to load equipment.</Alert>}

      {data && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Equipment #</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Make / Model</TableCell>
              <TableCell>Year</TableCell>
              <TableCell>Ownership</TableCell>
              <TableCell>Hourly Rate</TableCell>
              <TableCell>Daily Rate</TableCell>
              <TableCell>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((eq) => (
              <TableRow key={eq.id} hover>
                <TableCell sx={{ fontFamily: "monospace" }}>{eq.equipment_no}</TableCell>
                <TableCell>{eq.name}</TableCell>
                <TableCell>{[eq.make, eq.model].filter(Boolean).join(" ") || "—"}</TableCell>
                <TableCell>{eq.year ?? "—"}</TableCell>
                <TableCell>{eq.ownership_type}</TableCell>
                <TableCell>{fmtMoney(eq.hourly_rate)}</TableCell>
                <TableCell>{fmtMoney(eq.daily_rate)}</TableCell>
                <TableCell>
                  <Chip label={eq.status} color={STATUS_COLOR[eq.status]} size="small" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </Box>
  );
}
